// 工事一覧の表示モードに合わせて、データ取得を切り替える

import type { ConstructionListInfo, ConstructionMemberInfo, ConstructionAccountInfo, InvitedConstructionInfo } from "@/types/constructionApiDef"

import { defineStore } from "pinia"
import { ref, computed, watch } from 'vue'
import { ga4Event } from "@/utils/gtag_utils"
import { useApiStore, apiIdOf, API_ORIGIN, axiosRetryx } from "@/stores/api"
import { OK, apiDefaultDescriptions, type ApiOptions } from "@/stores/api"
import { getKnackRevision } from '@/utils/kuraemon'
import { useLoginStore } from "@/stores/login"
import { usePollingsStore, POLLING_LABELS } from "@/stores/pollings"
import { eachSlice } from "@/utils/collections"

export type KokubanTreeNode = {
  node_id: string
  node_name: string
  is_leaf: boolean
  kokuban_count?: number
  children: KokubanTreeNode[]
}

export type ViewMode = 'tile' | 'list'

export type ConstructionListViewModeMap = {
  [accountId: string]: {
    viewMode: ViewMode
  }
}

/** LastAccess: 閲覧順, ConstructionName: 工事名順, CreateAt: 作成順  */
export type ConstructionSortType = 'LastAccess' | 'ConstructionName' | 'CreateAt'

export type ConstructionSortTypeMap = {
  [accountId: string]: {
    constructionSortType: ConstructionSortType
  }
}

/** 空白文字で区切る処理 */
const WhitespaceUtil = {
  _deleimiterRegexp: /[ \u3000]+/g, // u3000 全角スペース
  normalize(s: string) {
    return s.trim().replace(WhitespaceUtil._deleimiterRegexp, ' ')
  },
  toArray(s: string) {
    return s.split(WhitespaceUtil._deleimiterRegexp).filter(s => s)
  },
}

export const useConstructionListsStore = defineStore(
  'constructionLists',
  () => {
    const { apiCall, checkResponseError } = useApiStore()
    const { currentAccount } = useLoginStore()

    const constructionListViewModeMap = ref<ConstructionListViewModeMap>({})

    const accountId = computed(() => {
      if(!currentAccount) return ''
      return currentAccount.account_id
    })

    const viewMode = computed(() => {
      if (!constructionListViewModeMap.value[accountId.value]) return 'tile';
      return constructionListViewModeMap.value[accountId.value].viewMode
    })

    const updateConstructionListViewMode = (viewMode: ViewMode = 'tile'): void  => {
      if (!constructionListViewModeMap.value[accountId.value]) {
        constructionListViewModeMap.value[accountId.value] = { viewMode } // 初期登録
        return
      }
      constructionListViewModeMap.value[accountId.value].viewMode = viewMode
    }

    // const cloudConstructionList = ref<Array<ConstructionListInfo> | null>(null)
    const isConstructionListLoaded = ref<boolean>(false)
    const isFetchingRemaining = ref<boolean>(false)
    const cloudConstructionList = ref<Array<ConstructionListInfo> | null | undefined>(null)
    const cloudConstructionListRevision = ref(0)

    /** クラウド工事一覧取得3API */
    const getCloudConstructionList3 = async ( args: { continue_token?: string, __apiOptions?: ApiOptions }) => {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getCloudConstructionList3');

      return await apiCall<{
        cloud_construction_list: Array<ConstructionListInfo>,
        continue_token?: string,
      }>(apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getcloudconstructionlist3";
        const data = {
          knack_revision: getKnackRevision(),
          //  写真整理情報ツリーは工事一覧では使わない ( 参照する工事ダイアログは専用APIで取得する ) 。
          //  全工事ぶんのツリーを受け取らないことで工事一覧を軽くする。
          skip_organize_info_tree: true,
          ...(args.continue_token ? { continue_token: args.continue_token } : {})
        }

        const retryxOptions = {
          maxTries: 3,
          timeout: 45000, // 45秒待つ
        }
        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig }, retryxOptions)

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(response, descriptions, apiOptions)) {
          return;
        }
        const apiResult = {
          cloud_construction_list: response.data.cloud_construction_list,
          ...(response.data.continue_token ? { continue_token: response.data.continue_token } : {})
        }
        return apiResult;
      })
    }

    /** クラウド工事メンバー一覧取得API */
    const getCloudConstructionMembers = async (ids: string[]) => {
      const apiOptions: ApiOptions = { noDisplayLoading: true }
      apiOptions.apiId = apiIdOf('getCloudConstructionMembers');

      return await apiCall<{
        construction_member_list: Array<ConstructionMemberInfo>,
      }>(apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/getcloudconstructionmembers";
        const data = {
          cloud_construction_ids: ids,
        }
        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }
        if (checkResponseError(response, descriptions, apiOptions)) {
          return;
        }
        return {
          construction_member_list: response.data.construction_member_list,
        }
      })
    }

    let __initPromise: Promise<Array<ConstructionListInfo> | null | undefined> | null = null

    const initCloudConstructionList = async (noDisplayLoading = false) => {
      if (!accountId.value) return // ログインアカウントがない場合は、取得しない

      // 既に初期化中の場合は同じPromiseを返す（2重呼び出し防止）
      if (__initPromise) return __initPromise

      // 既に取得中の場合は重複実行を防ぐ
      if (isFetchingRemaining.value) return cloudConstructionList.value

      __initPromise = (async () => {
        try {
          isConstructionListLoaded.value = false
          constructionMemberMap.value = {}
          const apiArgs = { __apiOptions: { noDisplayLoading } }

          const result = await getCloudConstructionList3(apiArgs)
          if (!result) return null

          // 最初の結果をすぐにセット
          cloudConstructionList.value = result.cloud_construction_list ? result.cloud_construction_list : []
          cloudConstructionListRevision.value++

          // continue_tokenがある場合は、バックグラウンドで続きを取得
          if ("continue_token" in result && result.continue_token) {
            const continueToken = result.continue_token
            setTimeout(() => {
              fetchRemainingConstructionList(continueToken)
            }, 0)
          } else {
            // continue_tokenがない場合は、すべてのデータが取得完了
            isConstructionListLoaded.value = true
          }

          return cloudConstructionList.value
        } finally {
          __initPromise = null
        }
      })()
      return __initPromise
    }

    const fetchRemainingConstructionList = async (continueToken: string) => {
      // 既に実行中の場合は重複実行を防ぐ
      if (isFetchingRemaining.value) return
      
      isFetchingRemaining.value = true
      let currentToken: string | undefined = continueToken
      
      try {
        while (currentToken) {
          const apiArgs = { 
            __apiOptions: { noDisplayLoading: true },
            continue_token: currentToken
          }
          
          const result = await getCloudConstructionList3(apiArgs)
          if (!result) break
          
          // 取得したリストを既存のリストに追加
          if (result.cloud_construction_list && cloudConstructionList.value) {
            cloudConstructionList.value.push(...result.cloud_construction_list)
            cloudConstructionListRevision.value++
          }
          
          currentToken = result.continue_token
        }
        
        // すべてのデータの取得が完了
        isConstructionListLoaded.value = true
      } finally {
        isFetchingRemaining.value = false
      }
    }

    const lastAccessConstruction = computed(() => {
      if (!cloudConstructionList.value) return null
      if (cloudConstructionList.value.length < 1) return null
  
      // 直近で閲覧した工事
      return cloudConstructionList.value.reduce((a, b) => {
        return a.lastaccess_at >= b.lastaccess_at ? a : b;
      })
    })

    const constructionSortTypeMap = ref<ConstructionSortTypeMap>({})

    const constructionSortType = computed<ConstructionSortType>(() => {
      if (!constructionSortTypeMap.value[accountId.value]) return 'LastAccess';
      return constructionSortTypeMap.value[accountId.value].constructionSortType
    })

    const updateConstructionSortType = (constructionSortType: ConstructionSortType = 'LastAccess'): void  => {
      if (!constructionSortTypeMap.value[accountId.value]) {
        constructionSortTypeMap.value[accountId.value] = { constructionSortType } // 初期登録
        return
      }
      constructionSortTypeMap.value[accountId.value].constructionSortType = constructionSortType
    }

    // ソートルール
    const SortRules = {
      // 閲覧順
      'LastAccess':
        (a: ConstructionListInfo, b: ConstructionListInfo) => (b.lastaccess_at - a.lastaccess_at),
      // 工事名順
      'ConstructionName':
        (a: ConstructionListInfo, b: ConstructionListInfo) => ((a.management_cloud_construction_name > b.management_cloud_construction_name) ? 1 : -1),
      // 作成順
      'CreateAt':
        (a: ConstructionListInfo, b: ConstructionListInfo) => (b.created_at - a.created_at),
    };
    const constructionSortRule = computed(() =>(SortRules[constructionSortType.value]))

    /**
     * 検索条件文字列に対する工事を検索するフィルタールールを返す
     * @param searchWord 検索条件文字列
     * @returns 工事を検索するフィルタールール
     */
    const constructionFilterRuleFor = (searchWord: string) => {
      return (constuction: {
        cloud_construction_name: string,
        management_cloud_construction_name: string,
      }) => {
        const list = WhitespaceUtil.toArray(searchWord)
        if (list.length < 1) return true // 検索条件指定なしの場合は全ヒット

        if (list.every(s => constuction.cloud_construction_name.includes(s))) return true
        if (list.every(s => constuction.management_cloud_construction_name.includes(s))) return true

        return false
      }
    }

    /** 検索ワード(アカウントID毎のマップ) */
    const searchConstructionWordMap = ref<{[accountId: string]: string}>({})

    /** 検索ワード */
    const searchConstructionWord = computed({
      get() {
        if (!accountId.value) return ""
        return searchConstructionWordMap.value[accountId.value] || ""
      },
      set(newWord: string) {
        if (!accountId.value) return
        searchConstructionWordMap.value[accountId.value] = newWord // ここはnormalizeしない(編集枠に出す)
      }
    })

    /** 検索フィルタされた工事一覧 */
    const filteredCloudConstructionList = computed(() => {
      if (!cloudConstructionList.value) return null

      if (!searchConstructionWord.value) return cloudConstructionList.value

      return cloudConstructionList.value.filter(
        constructionFilterRuleFor(searchConstructionWord.value)
      )
    })

    /** 検索履歴(アカウントID毎のマップ) */
    const searchConstructionWordHistoryMap = ref<{[accountId: string]: string[]}>({})

    /** 検索履歴 */
    const searchConstructionWordHistory = computed(() => {
      if (!accountId.value) return []
      return searchConstructionWordHistoryMap.value[accountId.value] || []
    })

    /** 検索履歴を記録する */
    const maxSearchHistoryCount = 3
    watch (
      () => searchConstructionWord.value,
      (newSearchWord: string) => {
        if (!newSearchWord) return
        if (!newSearchWord.trim()) return
        if (!accountId.value) return

        const addingWord = WhitespaceUtil.normalize(newSearchWord)

        const curSearchHistory = searchConstructionWordHistoryMap.value[accountId.value] || []
        const added = [ addingWord, ...curSearchHistory ]
        const uniq = [...new Set(added)]
        const limited = uniq.slice(0, maxSearchHistoryCount);
        searchConstructionWordHistoryMap.value[accountId.value] = limited
      }
    )

    /**
     * 候補工事名(管理用工事名)リストを返す
     * @param searchWord 検索条件文字列
     * @returns 候補工事名(管理用工事名)リスト
     */
    const getSuggestConstructionNames = (searchWord: string) => {
      if (!cloudConstructionList.value) return []

      if (!searchWord) return []

      const names = cloudConstructionList.value
        .filter(constructionFilterRuleFor(searchWord))
        .sort(constructionSortRule.value)
        .map(c => c.management_cloud_construction_name)

      const uniqueNames = [...new Set(names)]
      return uniqueNames
    }

    const noConstructionFilterMatch = computed(() => {
      if (!cloudConstructionList.value) return false

      // 全工事がない場合は、違う
      if (cloudConstructionList.value.length === 0) return false

      if (!filteredCloudConstructionList.value) return false

      // フィルターした件数が0の場合
      return (filteredCloudConstructionList.value.length === 0)
    })

    const logConstructionSearch = () => {
      ga4Event(
        '工事検索',
        { 
          screen_name: `W05-工事一覧`,
          event_action: searchConstructionWord.value,
          value: (filteredCloudConstructionList.value ? filteredCloudConstructionList.value.length : 0),
        }
      )
    }

    const initConstructionFilterFor = (constructionId: string) => {
      if (!constructionId) return

      if (!cloudConstructionList.value) return
      const existConstuction = cloudConstructionList.value.find(c => c.cloud_construction_id === constructionId)
      if (!existConstuction) return

      if (!filteredCloudConstructionList.value) return
      const displayConstruction = filteredCloudConstructionList.value.find(c => c.cloud_construction_id === constructionId)
      if (!displayConstruction) {
        // 指定工事IDが表示対象でない場合は、検索ワードをクリアする
        searchConstructionWord.value = ""
        return
      }
    }

    // ── 工事メンバー lazy ロード ──

    /** 工事メンバー情報マップ（工事ID → メンバー情報） */
    const constructionMemberMap = ref<Record<string, ConstructionMemberInfo>>({})

    /** 画面に表示中の工事IDフィルタ（null=フィルタなし） */
    const visibleConstructionIds = ref<Record<string, true> | null>(null)
    const setVisibleConstructionIds = (ids: string[]) => {
      const map: Record<string, true> = {}
      ids.forEach(id => { map[id] = true })
      visibleConstructionIds.value = map
    }
    const clearVisibleConstructionIds = () => {
      visibleConstructionIds.value = null
    }

    /** フェッチ中の工事ID（重複リクエスト防止用） */
    const __fetchingMemberIds = new Set<string>()

    /** 未取得の工事メンバーを取得する（10件ずつバッチ分割） */
    const fetchConstructionMembers = async (ids: string[]) => {
      // 取得済み・フェッチ中のIDをスキップ
      const unfetchedIds = ids.filter(id =>
        !constructionMemberMap.value[id] && !__fetchingMemberIds.has(id)
      )
      if (unfetchedIds.length === 0) return

      unfetchedIds.forEach(id => __fetchingMemberIds.add(id))

      // 10件ずつバッチに分割して順次取得
      for (const batch of eachSlice(unfetchedIds, 10)) {
        try {
          const result = await getCloudConstructionMembers(batch)
          if (!result) continue

          const newMap = { ...constructionMemberMap.value }
          result.construction_member_list.forEach(member => {
            newMap[member.cloud_construction_id] = member
          })
          constructionMemberMap.value = newMap
        } finally {
          batch.forEach(id => __fetchingMemberIds.delete(id))
        }
      }
    }

    /** 指定した工事のメンバー情報キャッシュを破棄して再取得する（Pix参加承認後など、即時反映が必要な場合に使用） */
    const refreshConstructionMember = async (constructionId: string) => {
      const newMap = { ...constructionMemberMap.value }
      delete newMap[constructionId]
      constructionMemberMap.value = newMap
      await fetchConstructionMembers([constructionId])
    }

    /** manager + normal の結合メンバーリスト（未ロード時はnull） */
    const getConstructionMemberAccountList = (constructionId: string): Array<ConstructionAccountInfo> | null => {
      const member = constructionMemberMap.value[constructionId]
      if (!member) return null
      return [...member.manager_account_info_list, ...member.normal_account_info_list]
    }

    /** メンバー数（未ロード時はnull） */
    const getConstructionMemberCount = (constructionId: string): number | null => {
      const member = constructionMemberMap.value[constructionId]
      if (!member) return null
      return member.manager_account_info_list.length + member.normal_account_info_list.length
    }

    // visibleIds 変更時にデバウンスしてメンバー取得
    const pollingsStore = usePollingsStore()
    watch(visibleConstructionIds, () => {
      pollingsStore.startDebounce(
        POLLING_LABELS.DEBOUNCE_FETCH_CONSTRUCTION_MEMBERS,
        async () => {
          if (!visibleConstructionIds.value) return
          const visibleIds = Object.keys(visibleConstructionIds.value)
          await fetchConstructionMembers(visibleIds)
        },
        300
      )
    })

    const copyableConstructionList = computed(() => {
      if (!cloudConstructionList.value) return []
      return cloudConstructionList.value
        .filter(c => {
          // 自身が「管理者」ではないは除外
          if (!c.is_current_user_manager) return false
          return true
        })
        .sort((a, b) => b.created_at - a.created_at)
    })

    const hasCopyableConstruction = computed(() => copyableConstructionList.value.length > 0)

    // ── 工事コピー: 黒板ツリー取得 ──

    const copySourceKokubanTree = ref<KokubanTreeNode[] | null>(null)
    const isFetchingCopySourceKokubanTree = ref(false)

    const fetchCopySourceKokubanTree = async (cloudConstructionId: string) => {
      isFetchingCopySourceKokubanTree.value = true
      copySourceKokubanTree.value = null
      try {
        const apiOptions: ApiOptions = { noDisplayLoading: true }
        apiOptions.apiId = apiIdOf('getCloudConstructionKokubanTree')
        const result = await apiCall<{ kokuban_tree_nodes: KokubanTreeNode[] }>(apiOptions, async (apiConfig) => {
          const url = API_ORIGIN + "/getcloudconstructionkokubantree"
          const data = { cloud_construction_id: cloudConstructionId }
          const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })
          const descriptions = { ...apiDefaultDescriptions }
          if (checkResponseError(response, descriptions, apiOptions)) return
          return { kokuban_tree_nodes: response.data.kokuban_tree_nodes }
        })
        if (result) {
          copySourceKokubanTree.value = result.kokuban_tree_nodes
        }
      } finally {
        isFetchingCopySourceKokubanTree.value = false
      }
    }

    const clearCopySourceKokubanTree = () => {
      copySourceKokubanTree.value = null
    }

    // ── 招待済み未参加工事 ──

    const invitedConstructionList = ref<InvitedConstructionInfo[]>([])

    /** フリープラン競合時の情報（201レスポンス時にセット、解消時にクリア） */
    const inviteFreePlanConflict = ref<{
      cloud_construction_id: string
      joining_cloud_construction_name: string
      invited_cloud_construction_name: string
    } | null>(null)

    /** 招待済み未参加工事一覧を取得する */
    const initInvitedConstructionList = async () => {
      const apiOptions: ApiOptions = { noDisplayLoading: true }
      apiOptions.apiId = apiIdOf('getInvitedCloudConstructionList')
      const result = await apiCall<{ invited_construction_list: InvitedConstructionInfo[] }>(apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + '/getinvitedcloudconstructionlist'
        const response = await axiosRetryx({ method: 'post', url, data: {}, ...apiConfig })
        if (checkResponseError(response, { ...apiDefaultDescriptions }, apiOptions)) return
        return { invited_construction_list: response.data.invited_construction_list }
      })
      invitedConstructionList.value = result?.invited_construction_list ?? []
    }

    type RespondResult =
      | { type: 'joined' }
      | { type: 'free_plan_conflict'; joining_cloud_construction_name: string; invited_cloud_construction_name: string }
      | { type: 'declined' }
      | { type: 'already_responded' }

    /** 招待に参加または辞退する */
    const respondCloudConstructionInvite = async (args: {
      cloud_construction_id: string
      action: 'join' | 'decline'
      force_exit?: boolean
    }) => {
      const FREE_PLAN_CONFLICT = 201
      // 招待に対してすでに「参加・辞退」の応答が完了している（他デバイス等で先に処理済みの場合。25172）
      const INVITE_ALREADY_DECLINED = 401039
      const INVITE_ALREADY_JOINED = 401040
      const apiOptions: ApiOptions = {}
      apiOptions.apiId = apiIdOf('respondCloudConstructionInvite')
      return await apiCall<RespondResult>(apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + '/respondcloudconstructioninvite'
        const data = {
          cloud_construction_id: args.cloud_construction_id,
          action: args.action,
          force_exit: args.force_exit ?? false,
          // Web側は新エラーコード(401039/401040)を受け取る（Nativeアプリと同時リリースできないため、Web側だけ明示的に要求する。25172）
          is_status_check_needed: true,
        }
        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })
        if (checkResponseError(
          response,
          { ...apiDefaultDescriptions },
          apiOptions,
          [OK, FREE_PLAN_CONFLICT, INVITE_ALREADY_DECLINED, INVITE_ALREADY_JOINED]
        )) return
        if (response.data.code === FREE_PLAN_CONFLICT) {
          return {
            type: 'free_plan_conflict' as const,
            joining_cloud_construction_name: response.data.joining_cloud_construction_name,
            invited_cloud_construction_name: response.data.invited_cloud_construction_name,
          }
        }
        if (response.data.code === INVITE_ALREADY_DECLINED || response.data.code === INVITE_ALREADY_JOINED) {
          return { type: 'already_responded' as const }
        }
        return { type: args.action === 'decline' ? 'declined' as const : 'joined' as const }
      })
    }

    return {
      viewMode,
      constructionListViewModeMap,
      updateConstructionListViewMode,
      cloudConstructionList,
      cloudConstructionListRevision,
      initCloudConstructionList,
      isConstructionListLoaded,
      isFetchingRemaining,
      lastAccessConstruction,

      constructionSortType,
      constructionSortTypeMap,
      updateConstructionSortType,
      constructionSortRule,

      filteredCloudConstructionList,
      getSuggestConstructionNames,
      searchConstructionWordMap,
      searchConstructionWord,
      searchConstructionWordHistoryMap,
      searchConstructionWordHistory,
      noConstructionFilterMatch,
      logConstructionSearch,
      initConstructionFilterFor,

      constructionMemberMap,
      visibleConstructionIds,
      setVisibleConstructionIds,
      clearVisibleConstructionIds,
      fetchConstructionMembers,
      refreshConstructionMember,
      getConstructionMemberAccountList,
      getConstructionMemberCount,

      copyableConstructionList,
      hasCopyableConstruction,

      copySourceKokubanTree,
      isFetchingCopySourceKokubanTree,
      fetchCopySourceKokubanTree,
      clearCopySourceKokubanTree,

      invitedConstructionList,
      inviteFreePlanConflict,
      initInvitedConstructionList,
      respondCloudConstructionInvite,
    }
  },
  {
    persist: {
      key: 'KuraemonCloud2_stores.constructionLists',
      storage: localStorage,
      paths: [
        'constructionListViewModeMap',
        'constructionSortTypeMap',
        'searchConstructionWordMap',
        'searchConstructionWordHistoryMap',
      ],
    }
  },
)
